import { GlobalAlert } from "../../components/alert/alert";
import React from "react";
import ReactDOM from "react-dom";
import styles from '../../components/alert/alert.module.scss'
import { MAX_ALERTS_COUNT, MAX_ALERTS_SHOW_BUTTON_CLOSE_ALL } from "../../constants";
import alertBus from "../../components/alert/alertBus";
import { v4 as uuidv4 } from "uuid";
import { UISettingsManager } from "../../libs/ui-settings-manager/UISettingsManager";
import { playAlertSound } from "../functions/beepSound";

export interface IAlertElementId {
    variant: AlertVariant,
    statusCode: number
    title: string
    detail?: string
    instance?: string
    durationMsec?: number
    isSound?: boolean
    filterError?: (detail: string, title?: string) => boolean
}

const activeAlerts = new Set<string>();
let closeAllButton: HTMLButtonElement | null = null;

const updateCloseAllButton = () => {

    const root = document.getElementById("globalAlert");
    if (!root) return;

    if (closeAllButton && closeAllButton.parentNode) {
        closeAllButton.parentNode.removeChild(closeAllButton);
        closeAllButton = null;
    }

    if (activeAlerts.size >= MAX_ALERTS_SHOW_BUTTON_CLOSE_ALL) {
        closeAllButton = document.createElement('button');
        closeAllButton.className = styles.buttonClose;
        closeAllButton.textContent = 'Закрыть все'
        
        closeAllButton.addEventListener('click', closeAllAlerts);
        root.appendChild(closeAllButton);
    }
};

const closeAllAlerts = () => {
    const root = document.getElementById("globalAlert");
    if (!root) return;

    const alertContainers = root.querySelectorAll(`.${styles.globalAlertItem}`);
    alertContainers.forEach(container => {
        ReactDOM.unmountComponentAtNode(container);
        container.remove();
    });

    activeAlerts.clear();

    if (closeAllButton && closeAllButton.parentNode) {
        closeAllButton.parentNode.removeChild(closeAllButton);
        closeAllButton = null;
    }

    alertBus.dispatchEvent(new CustomEvent("closeAllAlerts"));
};

const removeAlert = (alertId: string) => {
    activeAlerts.delete(alertId);
    updateCloseAllButton();
};

const renderGlobalAlert = (props: IAlertElementId, showDetails: boolean = false) => {
    const root = document.getElementById("globalAlert");

    if (!root) {
        console.error(`Не найден элемент с id=${"globalAlert"}`);
        return;
    }

    root.classList.remove('undefined');

    if (props.isSound) {
        if (props.variant === 'danger' || props.variant === 'error' || props.variant === "exception" || props.variant === 'warning') {
            playAlertSound("danger")
        }
    }

    const getAlertSettings = () => {
        const uiSettings = UISettingsManager.getSettings();
        return uiSettings.alerts;
        
    };

    let settings = getAlertSettings();

    const updateAllAlertsOpacity = () => {
        const alertItems = root.querySelectorAll(`.${styles.globalAlertItem}`);
        alertItems.forEach(item => {
            (item as HTMLElement).style.opacity = `${settings.opacity}`;
        });
    };

    const applyDynamicStyles = () => {

        updateAllAlertsOpacity()

        const existingStyle = document.getElementById('alert-dynamic-styles');
        if (existingStyle) {
            existingStyle.remove();
        }
        const style = document.createElement('style');
        style.id = 'alert-dynamic-styles';
        let css = ''
        if (settings.position === 'top') {
            css += `
                #globalAlert {
                    top: 105px;
                    bottom: auto;
                }
            `;
        } else {
            css += `
                #globalAlert {
                    bottom: 10px;
                    top: auto;
                }
            `;
        }
        if (settings.useAnimation) {
            const animationName = settings.position === 'top' ? 'slideFromTop' : 'slideFromBottom';
            
            css += `
                @keyframes slideFromTop {
                    from {
                        transform: translateY(-100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: ${settings.opacity};
                    }
                }
                
                @keyframes slideFromBottom {
                    from {
                        transform: translateY(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: ${settings.opacity};
                    }
                }
                
                #globalAlert .${styles.globalAlertItem} {
                    animation: ${animationName} 0.3s ease-out;
                }
            `;
        }
        style.textContent = css;
        document.head.appendChild(style);
    };

    applyDynamicStyles();
    
    const handleSettingsChange = () => {
        settings = getAlertSettings();
        applyDynamicStyles();
    };
    
    window.addEventListener('uiSettingsChanged', handleSettingsChange);

    root.classList.add(styles.globalAlertRoot);

    const alertId = uuidv4();
    activeAlerts.add(alertId);

    const container = document.createElement("div");
    container.className = styles.globalAlertItem;
    container.setAttribute('data-alert-id', alertId);
    container.style.opacity = `${settings.opacity}`;
    root.appendChild(container);

    const handleClose = () => {
        if(settings.useAnimation) {
            container.classList.add(styles.alertExitSandEffect)
            container.style.animation = 'collapse 0.4s ease-in forwards';
            container.style.pointerEvents = 'none';
        } else {
            container.style.opacity = '0';
        }
        
        const removeAfterAnimation = () => {
            ReactDOM.unmountComponentAtNode(container);
            container.remove();
            removeAlert(alertId);
            window.removeEventListener('uiSettingsChanged', handleSettingsChange);
        };
        
        if (settings.useAnimation) {
            container.addEventListener('animationend', removeAfterAnimation, { once: true });
            setTimeout(removeAfterAnimation, 500);
        } else {
            setTimeout(removeAfterAnimation, 100);
        }
    };

    ReactDOM.render(
        <React.StrictMode>
            <GlobalAlert
                variant={props.variant}
                statusCode={props.statusCode ?? 500}
                title={props.title}
                instance={props.instance}
                detail={props.detail}
                onClose={handleClose}
                container={container}
                alertElementId={alertId}
                showDetails={showDetails}
                durationMsec={props.durationMsec || settings.duration}
            />
        </React.StrictMode>,
        container
    );
            
    const children = root.querySelectorAll(`.${styles.globalAlertItem}`);
    if (children.length > MAX_ALERTS_COUNT) {
        const oldest = children[0];
        const oldestAlertId = oldest.getAttribute('data-alert-id');
        if (oldestAlertId) {
            activeAlerts.delete(oldestAlertId);
        }
        ReactDOM.unmountComponentAtNode(oldest);
        oldest.remove();
    }
    
    updateCloseAllButton();

    alertBus.addEventListener("alertClosed", ((event: CustomEvent<{ id: string }>) => {
        removeAlert(event.detail.id);
    }) as EventListener);


    alertBus.dispatchEvent(new CustomEvent("addAlert", { 
        detail: { 
            id: alertId,
            variant: props.variant,
            title: props.title,
            instance: props.instance,
            detail: props.detail,
            statusCode: props.statusCode
        } 
    }));
};

export default renderGlobalAlert;
